/**
 * Surveys and quick polls — Engage (ADR 0027).
 *
 * A SURVEY is sections of questions (1-5 rating, eNPS 0-10, single choice, multiple choice, free
 * text), each required or optional, sent to an audience between an open and a close date. A POLL
 * is a one-question survey (single or multiple choice) shown inline, with results right after
 * voting. Both are one table and one set of routes; `kind` tells them apart.
 *
 * AUDIENCE reuses ADR 0026's rule (`companyPolicyAppliesTo`): four id lists, all empty = the whole
 * organisation, AND across kinds, OR within one.
 *
 * ANONYMITY. An anonymous survey stores WHO completed it (for reminders and "you already answered")
 * apart from WHAT was answered, with no shared key and no timestamp on the answers, written in
 * separate transactions — the database itself cannot join them. Results of an anonymous survey,
 * for any slice (the whole audience, one legal entity, location or department), stay hidden until
 * that slice has at least `SURVEY_MIN_ANONYMOUS_RESPONSES` responses. Free text is never attributed.
 * Responding is hidden from the AI copilot: a copilot write is an `ai_proposals` row carrying the
 * caller and the request body, which would be exactly the link anonymity forbids.
 *
 * PERMISSIONS. Employees see what is open for them with `survey:read` and answer with
 * `survey:respond`; `/mine*` answers only for the caller. Everything else is `survey:manage`,
 * checked against the whole organisation (like `policy:manage`, ADR 0026 §6).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  uuidSchema,
} from '../schemas/common.js';
import {
  companyPolicyAudienceLabelsSchema,
  companyPolicyAudienceSchema,
} from './company-policy.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

/** Below this many responses, an anonymous survey's results for a slice are not shown. */
export const SURVEY_MIN_ANONYMOUS_RESPONSES = 5;

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

export const surveyKindSchema = z.enum(['survey', 'poll']);
export type SurveyKind = z.infer<typeof surveyKindSchema>;

export const surveyQuestionTypeSchema = z.enum([
  'rating',
  'enps',
  'single_choice',
  'multiple_choice',
  'text',
  /** Two fixed options, Yes and No; tallied like a single choice. */
  'yes_no',
  /** 1-5 agreement or satisfaction scale; stored and tallied like a rating. */
  'opinion_scale',
  /** A calendar date (YYYY-MM-DD). */
  'date',
  /** Free text, multi-line; tallied like free text. */
  'long_text',
  /** A single choice shown as a drop-down; tallied like a single choice. */
  'dropdown',
  /** Rows answered one column each; stored as rowId:columnId and tallied per row. */
  'matrix',
]);
export type SurveyQuestionType = z.infer<typeof surveyQuestionTypeSchema>;

/** Derived, never stored: `draft`, then `scheduled` before `opensOn`, `open`, `closed`. */
export const surveyStatusSchema = z.enum(['draft', 'scheduled', 'open', 'closed']);
export type SurveyStatus = z.infer<typeof surveyStatusSchema>;

/** Same audience rule as company policies (ADR 0026 §3). */
export const surveyAudienceSchema = companyPolicyAudienceSchema;
export type SurveyAudience = z.infer<typeof surveyAudienceSchema>;

export const surveyOptionSchema = z.object({
  /** Stable within the survey; answers refer to it, so relabelling keeps results. */
  id: z.string().min(1).max(40),
  label: z.string().trim().min(1).max(200),
});
export type SurveyOption = z.infer<typeof surveyOptionSchema>;

/** A matrix's rows and columns. Ids contain no colon (answers join them as rowId:columnId). */
export const surveyMatrixSchema = z.object({
  rows: z.array(surveyOptionSchema),
  columns: z.array(surveyOptionSchema),
});
export type SurveyMatrix = z.infer<typeof surveyMatrixSchema>;

export const surveyQuestionSchema = z.object({
  id: uuidSchema,
  type: surveyQuestionTypeSchema,
  prompt: z.string(),
  helpText: z.string().nullable(),
  required: z.boolean(),
  /** Choice questions (and an opinion scale's two end labels, ids low and high); empty otherwise. */
  options: z.array(surveyOptionSchema),
  /** Matrix questions only; null otherwise. */
  matrix: surveyMatrixSchema.nullable(),
});
export type SurveyQuestion = z.infer<typeof surveyQuestionSchema>;

export const surveySectionSchema = z.object({
  id: uuidSchema,
  /** Null for the implicit single section of a survey without sections. */
  title: z.string().nullable(),
  description: z.string().nullable(),
  questions: z.array(surveyQuestionSchema),
});
export type SurveySection = z.infer<typeof surveySectionSchema>;

export const surveySummarySchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  kind: surveyKindSchema,
  title: z.string(),
  description: z.string().nullable(),
  anonymous: z.boolean(),
  status: surveyStatusSchema,
  /** First day it is open, in each respondent's location timezone. */
  opensOn: dateOnlySchema,
  /** Last day it is open (inclusive); null only for a poll left open until closed by hand. */
  closesOn: dateOnlySchema.nullable(),
  /** Closed by hand before `closesOn`. */
  closedAt: instantSchema.nullable(),
  /** Remind non-responders every N days while open; null = no automatic reminders. */
  reminderEveryDays: z.number().int().positive().nullable(),
  audience: surveyAudienceSchema,
  questionCount: z.number().int().nonnegative(),
  /** People in today's audience, and how many of them have responded. */
  audienceSize: z.number().int().nonnegative(),
  responseCount: z.number().int().nonnegative(),
  publishedAt: instantSchema.nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type SurveySummary = z.infer<typeof surveySummarySchema>;

export const surveyDetailSchema = surveySummarySchema.extend({
  sections: z.array(surveySectionSchema),
  audienceLabels: companyPolicyAudienceLabelsSchema,
});
export type SurveyDetail = z.infer<typeof surveyDetailSchema>;

// ---- Results ---------------------------------------------------------------------------------

export const surveyCountSchema = z.object({
  /** `1`..`5` for rating, `0`..`10` for eNPS, an option id for choices. */
  value: z.string(),
  label: z.string(),
  count: z.number().int().nonnegative(),
});

export const surveyTextAnswerSchema = z.object({
  text: z.string(),
  /** Always null on an anonymous survey. */
  respondentName: z.string().nullable(),
});

/** One row of a matrix question, tallied across its columns. */
export const surveyMatrixRowResultSchema = z.object({
  rowId: z.string(),
  label: z.string(),
  /** Responses that answered this row. Zero when this row is suppressed. */
  answered: z.number().int().nonnegative(),
  /** Anonymous and fewer than the threshold answered this row: no counts are shown. */
  suppressed: z.boolean(),
  /** One entry per column, zeros included. */
  distribution: z.array(surveyCountSchema),
});
export type SurveyMatrixRowResult = z.infer<typeof surveyMatrixRowResultSchema>;

export const surveyQuestionResultSchema = z.object({
  questionId: uuidSchema,
  sectionTitle: z.string().nullable(),
  type: surveyQuestionTypeSchema,
  prompt: z.string(),
  /** Anonymous and too few answers to show without exposing someone: no tally, no text. */
  suppressed: z.boolean(),
  /** Responses that answered this question. Zero when suppressed. */
  answered: z.number().int().nonnegative(),
  /** Every scale point / option, zeros included, in display order. Empty for free text, dates
   *  and matrices (see matrixRows). */
  distribution: z.array(surveyCountSchema),
  /** Rating and opinion scale (1-5); null otherwise or with no answers. */
  average: z.number().nullable(),
  /** eNPS only: % promoters (9-10) minus % detractors (0-6), -100..100. */
  enps: z
    .object({
      score: z.number().int(),
      promoters: z.number().int().nonnegative(),
      passives: z.number().int().nonnegative(),
      detractors: z.number().int().nonnegative(),
    })
    .nullable(),
  /** Free text and long text only; unattributed and in no meaningful order on an anonymous survey. */
  textAnswers: z.array(surveyTextAnswerSchema),
  /** Date only: every answer as YYYY-MM-DD, sorted ascending. Unattributed. */
  dateAnswers: z.array(z.string()),
  /** Matrix only: one entry per row, in display order. Empty otherwise. */
  matrixRows: z.array(surveyMatrixRowResultSchema),
});
export type SurveyQuestionResult = z.infer<typeof surveyQuestionResultSchema>;

export const surveySliceKindSchema = z.enum(['all', 'legal_entity', 'location', 'department']);
export type SurveySliceKind = z.infer<typeof surveySliceKindSchema>;

export const surveySliceSchema = z.object({
  by: surveySliceKindSchema,
  /** Null for `all` and for "not placed" (no location / department on record). */
  id: uuidSchema.nullable(),
  name: z.string(),
  audienceSize: z.number().int().nonnegative(),
  /** Completions in this slice, from today's placement of each respondent. Null when hidden. */
  completed: z.number().int().nonnegative().nullable(),
  /** Answer sets in this slice; null when hidden by the anonymity threshold. */
  responses: z.number().int().nonnegative().nullable(),
  /** Anonymous and fewer than the threshold: no result is shown for this slice. */
  belowThreshold: z.boolean(),
});
export type SurveySlice = z.infer<typeof surveySliceSchema>;

export const surveyResultsSchema = z.object({
  survey: surveySummarySchema,
  minimumResponses: z.number().int().positive(),
  /** False for an anonymous survey that has not closed: no slice or question data is included. */
  resultsReleased: z.boolean(),
  /** 0..1 over today's audience; null with an empty audience. */
  responseRate: z.number().min(0).max(1).nullable(),
  /** The slice these results are for. */
  slice: surveySliceSchema,
  /** Every slice of the kind asked for (legal entities, locations or departments). */
  slices: z.array(surveySliceSchema),
  /** Empty when `slice.belowThreshold`. */
  questions: z.array(surveyQuestionResultSchema),
});
export type SurveyResults = z.infer<typeof surveyResultsSchema>;

// ---- The employee's view ---------------------------------------------------------------------

/** A poll's tally as a voter sees it. */
export const pollResultsSchema = z.object({
  total: z.number().int().nonnegative(),
  /** Null while an anonymous poll has fewer votes than the threshold. */
  distribution: z.array(surveyCountSchema).nullable(),
});
export type PollResults = z.infer<typeof pollResultsSchema>;

export const mySurveySchema = z.object({
  id: uuidSchema,
  kind: surveyKindSchema,
  title: z.string(),
  description: z.string().nullable(),
  anonymous: z.boolean(),
  opensOn: dateOnlySchema,
  closesOn: dateOnlySchema.nullable(),
  questionCount: z.number().int().nonnegative(),
  /** When the caller responded; null if they have not. */
  completedAt: instantSchema.nullable(),
  /** Polls only: the one question, so it can be answered inline. */
  pollQuestion: surveyQuestionSchema.nullable(),
  /** Polls only, and only once the caller has voted. */
  pollResults: pollResultsSchema.nullable(),
  /** Polls only: how many people have voted so far — visible before the caller votes, unlike
   *  `pollResults`. A count, never the distribution, so it does not leak the anonymous breakdown
   *  early. Null for a `kind: 'survey'` entry. */
  voteCount: z.number().int().nonnegative().nullable(),
});
export type MySurvey = z.infer<typeof mySurveySchema>;

export const mySurveyDetailSchema = mySurveySchema.extend({
  sections: z.array(surveySectionSchema),
});
export type MySurveyDetail = z.infer<typeof mySurveyDetailSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .optional();

const matrixOptionSchema = surveyOptionSchema.refine((o) => !o.id.includes(':'), {
  message: 'Option ids cannot contain a colon',
});

export const surveyQuestionInputSchema = z.object({
  /** Keep a question's id across saves; a new question omits it. */
  id: uuidSchema.optional(),
  type: surveyQuestionTypeSchema,
  prompt: z.string().trim().min(1).max(500),
  helpText: optionalText(1000),
  required: z.boolean().default(true),
  /**
   * Choice questions. Yes/no options are fixed by the server and may be omitted. An opinion
   * scale may send its two end labels with the ids `low` and `high`.
   */
  options: z.array(surveyOptionSchema).max(20).default([]),
  /** Matrix questions only: 1-25 rows and 2-10 columns (checked on publish). */
  matrix: z
    .object({
      rows: z.array(matrixOptionSchema).max(25),
      columns: z.array(matrixOptionSchema).max(10),
    })
    .nullable()
    .optional(),
});
export type SurveyQuestionInput = z.infer<typeof surveyQuestionInputSchema>;

export const surveySectionInputSchema = z.object({
  title: optionalText(200),
  description: optionalText(1000),
  questions: z.array(surveyQuestionInputSchema).max(100),
});

/** A draft may be incomplete; `publishSurvey` is where completeness is enforced. */
export const surveyDraftBodySchema = z.object({
  kind: surveyKindSchema.default('survey'),
  title: z.string().trim().min(1).max(200),
  description: optionalText(2000),
  /** Polls default to anonymous in the builder; the server takes what it is sent. */
  anonymous: z.boolean().default(false),
  opensOn: dateOnlySchema,
  closesOn: dateOnlySchema.nullable().optional(),
  reminderEveryDays: z.number().int().min(1).max(30).nullable().optional(),
  audience: surveyAudienceSchema.default({}),
  /** One section with a null title is a survey without sections. */
  sections: z.array(surveySectionInputSchema).max(20).default([]),
});
export type SurveyDraftBody = z.infer<typeof surveyDraftBodySchema>;

/** After publication only the close date and the reminders can change. */
export const surveyScheduleBodySchema = z.object({
  closesOn: dateOnlySchema.nullable(),
  reminderEveryDays: z.number().int().min(1).max(30).nullable(),
});

/** One answer. Which field is read depends on the question's type; unanswered optional = omitted. */
export const surveyAnswerInputSchema = z.object({
  questionId: uuidSchema,
  /** rating and opinion_scale (1-5), enps (0-10). */
  number: z.number().int().min(0).max(10).optional(),
  /** single_choice, yes_no and dropdown (exactly one); multiple_choice (one or more). */
  optionIds: z.array(z.string().min(1).max(40)).max(20).optional(),
  /** text and long_text. */
  text: z.string().trim().max(5000).optional(),
  /** date: YYYY-MM-DD. */
  date: dateOnlySchema.optional(),
  /** matrix: one column for each row that is answered. */
  matrix: z
    .array(
      z.object({
        rowId: z.string().min(1).max(40),
        columnId: z.string().min(1).max(40),
      }),
    )
    .max(50)
    .optional(),
});
export type SurveyAnswerInput = z.infer<typeof surveyAnswerInputSchema>;

export const submitSurveyResponseBodySchema = z.object({
  answers: z.array(surveyAnswerInputSchema).max(200),
});

const listQuerySchema = z.object({
  kind: z.enum(['all', 'survey', 'poll']).default('all'),
  status: z.enum(['all', 'draft', 'scheduled', 'open', 'closed']).default('all'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

const resultsQuerySchema = z.object({
  sliceBy: surveySliceKindSchema.default('all'),
  /** With `sliceBy` other than `all`: the entity / location / department; omitted = unplaced. */
  sliceId: uuidSchema.optional(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const manage = routeMeta('survey:manage');

export const surveyContract = c.router(
  {
    // ---- Employee self-service (literal `/mine` paths first: see contract-route-order.test) ----
    listMySurveys: {
      method: 'GET',
      path: '/mine',
      query: z.object({ kind: z.enum(['all', 'survey', 'poll']).default('all') }),
      responses: {
        200: z.object({
          items: z.array(mySurveySchema),
          pendingCount: z.number().int().nonnegative(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Open surveys and polls sent to me, and whether I have answered them',
      metadata: routeMeta('survey:read'),
    },
    getMySurvey: {
      method: 'GET',
      path: '/mine/:surveyId',
      responses: {
        200: mySurveyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One open survey sent to me, with its questions',
      metadata: routeMeta('survey:read'),
    },
    submitMyResponse: {
      method: 'POST',
      path: '/mine/:surveyId/responses',
      body: submitSurveyResponseBodySchema,
      responses: {
        201: mySurveySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Already answered, or the survey is no longer open. */
        409: errorBodySchema,
        /** A required question is unanswered, or an answer does not fit its question. */
        422: errorBodySchema,
      },
      summary: 'Answer a survey or vote in a poll',
      // A copilot write is an ai_proposals row holding the caller AND the body — the link an
      // anonymous survey exists to prevent. People answer surveys themselves.
      metadata: routeMeta('survey:respond', { ai: 'hidden' }),
    },

    // ---- Administration ------------------------------------------------------------------------
    listSurveys: {
      method: 'GET',
      path: '/',
      query: listQuerySchema,
      responses: {
        200: z.object({
          items: z.array(surveySummarySchema),
          /** Matching the filters, across all pages. */
          total: z.number().int().nonnegative(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Surveys and polls with their status and response count, paged',
      metadata: manage,
    },
    createSurvey: {
      method: 'POST',
      path: '/',
      body: surveyDraftBodySchema,
      responses: {
        201: surveyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Start a new survey or poll as a draft',
      metadata: manage,
    },
    estimateSurveyReach: {
      method: 'POST',
      path: '/estimate-reach',
      body: z.object({ audience: surveyAudienceSchema }),
      responses: {
        200: z.object({ count: z.number().int().nonnegative() }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'How many people an audience currently matches, before saving a draft',
      metadata: manage,
    },
    getSurvey: {
      method: 'GET',
      path: '/:surveyId',
      responses: {
        200: surveyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'A survey with its sections and questions',
      metadata: manage,
    },
    saveDraft: {
      method: 'PUT',
      path: '/:surveyId',
      body: surveyDraftBodySchema,
      responses: {
        200: surveyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Published: questions are frozen once anyone may have answered. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Replace a draft survey (questions, audience, dates)',
      metadata: manage,
    },
    deleteDraft: {
      method: 'DELETE',
      path: '/:surveyId',
      responses: {
        200: z.object({ deleted: z.literal(true) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Delete a survey that was never published',
      metadata: manage,
    },
    publishSurvey: {
      method: 'POST',
      path: '/:surveyId/publish',
      body: z.object({ notify: z.boolean().default(true) }),
      responses: {
        200: surveyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** Incomplete: no questions, a choice question without two options, bad dates. */
        422: errorBodySchema,
      },
      summary: 'Publish a draft survey to its audience',
      metadata: manage,
    },
    updateSchedule: {
      method: 'PUT',
      path: '/:surveyId/schedule',
      body: surveyScheduleBodySchema,
      responses: {
        200: surveyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Change the close date or reminders of a published survey',
      metadata: manage,
    },
    closeSurvey: {
      method: 'POST',
      path: '/:surveyId/close',
      body: z.object({}).default({}),
      responses: {
        200: surveyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Close a survey now, before its close date',
      metadata: manage,
    },
    sendReminders: {
      method: 'POST',
      path: '/:surveyId/remind',
      body: z.object({}).default({}),
      responses: {
        200: z.object({ notified: z.number().int().nonnegative() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not open. */
        409: errorBodySchema,
      },
      summary: 'Remind everyone in the audience who has not responded yet',
      metadata: manage,
    },
    getResults: {
      method: 'GET',
      path: '/:surveyId/results',
      query: resultsQuerySchema,
      responses: {
        200: surveyResultsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Response rate and per-question results, overall or for one slice',
      // Employees' free-text answers (attributed on a named survey) are confidential content.
      metadata: routeMeta('survey:manage', { ai: 'hidden' }),
    },
    duplicateSurvey: {
      method: 'POST',
      path: '/:surveyId/duplicate',
      body: z.object({}).default({}),
      responses: {
        201: surveyDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Copy a survey or poll into a new draft (questions and audience; no responses)',
      metadata: manage,
    },
    exportResults: {
      method: 'GET',
      path: '/:surveyId/results/export',
      responses: {
        200: z.object({
          fileName: z.string(),
          mimeType: z.literal('text/csv'),
          content: z.string(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Anonymous with fewer responses than the threshold. */
        409: errorBodySchema,
      },
      summary: 'Every response as CSV (unattributed on an anonymous survey)',
      metadata: routeMeta('survey:manage', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/surveys' },
);

// ---------------------------------------------------------------------------
// Survey templates (ADR 0027): reusable draft bodies, org scoped. A separate router so the
// literal /surveys routes above keep their order; mounted at /survey-templates.
// ---------------------------------------------------------------------------

export const surveyTemplateSummarySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  kind: surveyKindSchema,
  questionCount: z.number().int().nonnegative(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type SurveyTemplateSummary = z.infer<typeof surveyTemplateSummarySchema>;

export const surveyTemplateSchema = surveyTemplateSummarySchema.extend({
  /** The draft body a new survey starts from. Its dates are not reused: set them on the copy. */
  body: surveyDraftBodySchema,
});
export type SurveyTemplate = z.infer<typeof surveyTemplateSchema>;

export const surveyTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: optionalText(1000),
  body: surveyDraftBodySchema,
});
export type SurveyTemplateBody = z.infer<typeof surveyTemplateBodySchema>;

const templateManage = routeMeta('survey:manage');

export const surveyTemplateContract = c.router(
  {
    listSurveyTemplates: {
      method: 'GET',
      path: '/',
      responses: {
        200: z.object({ items: z.array(surveyTemplateSummarySchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Survey templates saved in this organisation',
      metadata: templateManage,
    },
    createSurveyTemplate: {
      method: 'POST',
      path: '/',
      body: surveyTemplateBodySchema,
      responses: {
        201: surveyTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Save a draft body as a reusable template',
      metadata: templateManage,
    },
    getSurveyTemplate: {
      method: 'GET',
      path: '/:templateId',
      responses: {
        200: surveyTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One survey template with its questions',
      metadata: templateManage,
    },
    updateSurveyTemplate: {
      method: 'PUT',
      path: '/:templateId',
      body: surveyTemplateBodySchema,
      responses: {
        200: surveyTemplateSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Replace a survey template',
      metadata: templateManage,
    },
    deleteSurveyTemplate: {
      method: 'DELETE',
      path: '/:templateId',
      responses: {
        200: z.object({ deleted: z.literal(true) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Delete a survey template (surveys made from it are not affected)',
      metadata: templateManage,
    },
  },
  { pathPrefix: '/survey-templates' },
);

// ---------------------------------------------------------------------------
// Survey recurrences (pulse surveys): `apps/worker`'s survey-recurrence sweep publishes a fresh
// survey from `payload` every `everyDays` days, open for `durationDays`, with no admin touching it
// each cycle. Org scoped like templates above (NOT legal-entity-scoped: a pulse is an
// organisation-wide cadence, not a registered company's statutory calendar) — a separate router so
// the literal /surveys routes keep their order; mounted at /survey-recurrences. Admin-only
// (`survey:manage`) and never shown to an employee, same as templates.
// ---------------------------------------------------------------------------

export const surveyRecurrenceSummarySchema = z.object({
  id: uuidSchema,
  title: z.string(),
  everyDays: z.number().int().positive(),
  durationDays: z.number().int().positive(),
  /** The next day the sweep will publish a fresh survey from this recurrence, inclusive. */
  nextRunOn: dateOnlySchema,
  /** The last day it actually published one; null if it never has. */
  lastRunOn: dateOnlySchema.nullable(),
  /** Paused (false) recurrences are skipped by the sweep but keep their schedule. */
  active: z.boolean(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type SurveyRecurrenceSummary = z.infer<typeof surveyRecurrenceSummarySchema>;

/** The draft body a new survey starts from each time this recurrence fires. Its own `opensOn` /
 *  `closesOn` are never reused: the sweep sets them to that run's day and `day + durationDays`. */
export const surveyRecurrenceSchema = surveyRecurrenceSummarySchema.extend({
  payload: surveyDraftBodySchema,
});
export type SurveyRecurrence = z.infer<typeof surveyRecurrenceSchema>;

export const surveyRecurrenceBodySchema = z.object({
  title: z.string().trim().min(1).max(200),
  payload: surveyDraftBodySchema,
  /** How often a fresh survey is published. */
  everyDays: z.number().int().min(1).max(365),
  /** How many days each published survey stays open. */
  durationDays: z.number().int().min(1).max(365),
  /** The first day to publish on; omitted defaults to today on the server. */
  startOn: dateOnlySchema.nullable().optional(),
  active: z.boolean().default(true),
});
export type SurveyRecurrenceBody = z.infer<typeof surveyRecurrenceBodySchema>;

const recurrenceManage = routeMeta('survey:manage');

export const surveyRecurrenceContract = c.router(
  {
    listSurveyRecurrences: {
      method: 'GET',
      path: '/',
      responses: {
        200: z.object({ items: z.array(surveyRecurrenceSummarySchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Recurring pulse surveys configured in this organisation',
      metadata: recurrenceManage,
    },
    createSurveyRecurrence: {
      method: 'POST',
      path: '/',
      body: surveyRecurrenceBodySchema,
      responses: {
        201: surveyRecurrenceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Schedule a survey to publish itself on a repeating cadence',
      metadata: recurrenceManage,
    },
    getSurveyRecurrence: {
      method: 'GET',
      path: '/:recurrenceId',
      responses: {
        200: surveyRecurrenceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One recurring pulse survey, with the draft it publishes each cycle',
      metadata: recurrenceManage,
    },
    setSurveyRecurrenceActive: {
      method: 'PUT',
      path: '/:recurrenceId',
      body: z.object({ active: z.boolean() }),
      responses: {
        200: surveyRecurrenceSummarySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Pause or resume a recurring pulse survey',
      metadata: recurrenceManage,
    },
    deleteSurveyRecurrence: {
      method: 'DELETE',
      path: '/:recurrenceId',
      responses: {
        200: z.object({ deleted: z.literal(true) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Delete a recurring pulse survey (surveys it already published are unaffected)',
      metadata: recurrenceManage,
    },
  },
  { pathPrefix: '/survey-recurrences' },
);
